// The delivery days of usable data for a product in a country: the data behind the country chooser
// and the data coverage page. Only data the app can use counts, so a delivery on a blocked target (Latvian and
// Lithuanian Origin-Destination) is not counted even when it exists; a pending target counts. Pure: no React, router or Firebase imports.
import type { CountryCode } from '../config/countries';
import { capability, supportStatus } from '../config/products';
import type { DataProductId } from '../config/products';
import { datesFor } from './availability';
import type { AvailabilityIndex } from './availability';
import type { IsoDay } from './dateWindow';

/** Availability table names a product reads in a country, leaving out blocked targets. */
function usableTables(c: CountryCode, p: DataProductId): string[] {
  const cap = capability(c, p);
  if (!cap) return [];
  if (cap.product === 'flow') return supportStatus(c, p, null).ok ? Object.values(cap.tables) : [];
  const tables: string[] = [];
  for (const geo of cap.geo) {
    if (!supportStatus(c, p, geo.geoType).ok) continue;
    for (const table of Object.values(geo.tables)) if (table) tables.push(table);
  }
  return tables;
}

/** Distinct delivery days across the product's usable tables, ascending. */
export function productDays(c: CountryCode, p: DataProductId, index: AvailabilityIndex): IsoDay[] {
  const days = new Set<IsoDay>();
  for (const table of usableTables(c, p)) for (const day of datesFor(index, table)) days.add(day);
  return Array.from(days).sort();
}
